import axios from 'axios';
import { getApiHost } from 'src/utils/ssoReferrer';

export interface DivisionMembership {
  id: string;
  name: string;
  companyName: string;
  role: string;
  appAccess: Record<string, string>;
  isActive: boolean;
}

export interface V2LoginResponse {
  userId: string;
  email: string;
  name: string;
  divisions: DivisionMembership[];
  lastActiveDivisionId: string;
  sessionToken: string;
}

export interface V2TokenResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  divisionId: string;
  divisionName: string;
  // Optional so the frontend tolerates older backends mid-deploy
  // and falls back to a plain reload(). Populated when the division's
  // VendorId resolves to a positive int, otherwise omitted.
  defaultBeaconClientId?: number;
}

export interface V2ValidateResponse {
  authenticated: boolean;
  reason?: string;
  user?: {
    userId: string;
    email: string;
    name: string;
  };
  division?: DivisionMembership;
  divisions?: DivisionMembership[];
  sessionToken?: string;
}

/**
 * Lightweight liveness response. Returned by GET /v2/session-info.
 * Used by tab-focus / mismatch hooks that only need to know whether the
 * cookie's bound division has changed -- no DB lookup, no Redis write.
 */
export interface V2SessionInfoResponse {
  authenticated: boolean;
  reason?: string;
  divisionId?: string;
}

/**
 * One-shot session token response. Returned by POST /v2/session-token.
 * Used by the cross-app SSO no-access redirect flow that bridges into
 * POST /v2/token for a different division.
 */
export interface V2SessionTokenResponse {
  sessionToken: string;
  expiresIn: number;
}

export const v2LoginFederated = async (
  accessToken: string,
  idToken: string,
  referrer?: string
): Promise<V2LoginResponse> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/login/federated`, {
    accessToken,
    idToken,
    referrer
  });
  return response.data;
};

export const v2IssueToken = async (
  sessionToken: string,
  divisionId: string,
  referrer?: string
): Promise<V2TokenResponse> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/token`, {
    sessionToken,
    divisionId,
    referrer
  }, {
    withCredentials: true
  });
  return response.data;
};

export const v2GetDivisions = async (): Promise<{
  divisions: DivisionMembership[];
  activeDivisionId: string;
}> => {
  const baseUrl = getApiHost();
  const response = await axios.get(`https://${baseUrl}/api/auth/v2/divisions`);
  return response.data;
};

/**
 * POST /v2/switch-division -- atomic in-place division switch.
 * Single round trip; server revokes the old JTI and sets the new
 * sl-auth-token cookie via Set-Cookie. Returns the new bearer token plus
 * `defaultBeaconClientId` for /client/:id URL rewrites.
 *
 * Replaces the legacy v2Validate -> v2IssueToken pair which required two
 * round trips and didn't revoke the old token.
 */
export const v2SwitchDivision = async (newDivisionId: string): Promise<V2TokenResponse & { previousTokenRevoked: boolean }> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/switch-division`, {
    newDivisionId
  }, {
    withCredentials: true
  });
  return response.data;
};

/**
 * POST /v2/validate -- DEPRECATED. Bundles liveness + divisions list +
 * sessionToken into one heavy call. Prefer:
 *   - v2SessionInfo()      for liveness + active divisionId (focus checks)
 *   - v2GetDivisions()     for the divisions list
 *   - v2SessionToken()     for the cross-app SSO redirect flow
 *   - v2SwitchDivision()   for in-place division switching
 *
 * The sessionToken field is now opt-in via ?includeSessionToken=true on the
 * server, so calling without the flag returns null in that field.
 */
export const v2Validate = async (): Promise<V2ValidateResponse> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/validate`, {}, {
    withCredentials: true
  });
  return response.data;
};

/**
 * GET /v2/session-info -- lightweight liveness check.
 * Returns {authenticated, divisionId} from JWT verify + revocation check.
 * No DB lookup, no Redis write. Used by tab-focus and mismatch hooks.
 */
export const v2SessionInfo = async (): Promise<V2SessionInfoResponse> => {
  const baseUrl = getApiHost();
  try {
    const response = await axios.get(`https://${baseUrl}/api/auth/v2/session-info`, {
      withCredentials: true
    });
    return response.data;
  } catch (err: any) {
    return { authenticated: false, reason: err?.message || 'network error' };
  }
};

/**
 * POST /v2/session-token -- mint a one-shot session token for the
 * authenticated user. Used by the cross-app SSO no-access redirect flow that
 * bridges into POST /v2/token. Replaces the SessionToken side-effect that
 * used to be bundled into /v2/validate.
 */
export const v2SessionToken = async (): Promise<V2SessionTokenResponse | null> => {
  const baseUrl = getApiHost();
  try {
    const response = await axios.post(`https://${baseUrl}/api/auth/v2/session-token`, {}, {
      withCredentials: true
    });
    return response.data;
  } catch {
    return null;
  }
};

export const v2Logout = async (): Promise<{ success: boolean }> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/logout`);
  return response.data;
};

export const v2Refresh = async (refreshToken: string, divisionId: string): Promise<V2TokenResponse> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/refresh`, {
    refreshToken,
    divisionId
  });
  return response.data;
};

export const v2OtpSend = async (email: string): Promise<{ success: boolean }> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/otp/send`, { email });
  return response.data;
};

export const v2OtpVerify = async (email: string, code: string): Promise<V2LoginResponse> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/otp/verify`, { email, code });
  return response.data;
};

/**
 * Pick the default division for auto-login.
 * Backend already resolves lastActiveDivisionId with 3-tier fallback
 * (Redis last-active -> sl-users.divisionId -> oldest membership)
 * and returns divisions sorted by createdAt ascending.
 */
export function resolveDefaultDivision(
  divisions: DivisionMembership[],
  lastActiveDivisionId: string | undefined
): DivisionMembership {
  if (lastActiveDivisionId) {
    const preferred = divisions.find(d => d.id === lastActiveDivisionId);
    if (preferred) return preferred;
  }
  return divisions[0];
}

export const fetchSubscriptions = async (accessToken: string, appName: string): Promise<any> => {
  const baseUrl = getApiHost();
  const response = await axios.get(`https://${baseUrl}/api/auth/v2/subscriptions`, {
    params: { appName },
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });
  return response.data;
};
